import * as React from 'react';
import { useCallback, useMemo } from 'react';

import Box from '@mui/material/Box';
import isEmpty from 'lodash/isEmpty';

import {
  Alert,
  CheckBox,
  DetailPanelExpansionType,
  Divider,
  GridColDef,
  GridRowParams,
  GridValidRowModel,
  StateAwareGridPremium,
  useGridApiRef,
} from '@sbv-clients/ui-shared';
import { nameOfFactory } from '@sbv-clients/util-misc';

import { FacilityMenuByIdResponseType } from '../../services/admin';
import { ParentMenuWithSubMenuItemsType } from '../../utils/transform';
import DetailsTable from './details-table';
import { MenuPermissionsActionType } from './use-menu-permissions';

const menuKeys = nameOfFactory<ParentMenuWithSubMenuItemsType>();

interface FacilityMenuDataGridProps {
  data: ParentMenuWithSubMenuItemsType[];
  isLoading: boolean;
  actions: MenuPermissionsActionType;
  detailPanelExpansion: DetailPanelExpansionType;
  allowedItemsMap: Record<string, boolean>;
}

const FacilityMenuDataGrid = ({
  data,
  isLoading,
  actions,
  allowedItemsMap,
  detailPanelExpansion,
}: FacilityMenuDataGridProps) => {
  const apiRef = useGridApiRef();

  const columns = useMemo(() => {
    return [
      {
        field: menuKeys('order'),
        headerName: 'Order',
        width: 100,
        sortable: false,
      },
      {
        field: menuKeys('label'),
        headerName: 'Label',
        width: 200,
        sortable: false,
      },
      { field: menuKeys('link'), headerName: 'Link', flex: 1, sortable: false },
      {
        field: 'actions',
        headerName: 'Enabled',
        width: 320,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const id = params?.row?.id as string;
          const checked = allowedItemsMap[id] ?? false;
          return (
            <Box>
              <CheckBox
                size="small"
                name={id}
                checked={checked}
                onChange={actions.onChangeParentMenuItem}
              />
            </Box>
          );
        },
      },
    ] as GridColDef<FacilityMenuByIdResponseType>[];
  }, [actions.onChangeParentMenuItem, allowedItemsMap]);

  const getDetailPanelContent = useCallback(
    (params: GridRowParams<GridValidRowModel>) => {
      const row = params.row as ParentMenuWithSubMenuItemsType;
      return (
        <>
          <Box p={4} bgcolor="white">
            {isEmpty(row.children) ? (
              <Alert severity="info">No item found</Alert>
            ) : (
              <DetailsTable
                data={row}
                actions={actions}
                allowedItemsMap={allowedItemsMap}
              />
            )}
          </Box>
          <Divider />
        </>
      );
    },
    [actions, allowedItemsMap]
  );

  return (
    <Box sx={{ overflow: 'hidden', height: '100%' }}>
      <StateAwareGridPremium
        name="facility-menu-data-grid"
        apiRef={apiRef}
        rows={data}
        columns={columns}
        disableColumnMenu
        loading={isLoading}
        hideFooter
        getDetailPanelHeight={() => 'auto'}
        getDetailPanelContent={getDetailPanelContent}
        detailPanelExpandedRowIds={
          detailPanelExpansion.detailPanelExpandedRowIds
        }
        onDetailPanelExpandedRowIdsChange={
          detailPanelExpansion.onDetailPanelExpandedRowIdsChange
        }
      />
    </Box>
  );
};

export default FacilityMenuDataGrid;
